import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const SITE_URL = 'https://gate33.co';
const OWN = 'data-g33-head';

const canonicalUrl = (pathname) => {
  const p = (pathname || '/').replace(/\/+$/, '');
  return p ? `${SITE_URL}${p}` : `${SITE_URL}/`;
};

const metaKey = (m) => (m.getAttribute('property') ? `property:${m.getAttribute('property')}` : m.getAttribute('name') ? `name:${m.getAttribute('name')}` : null);

const isReact = (el) => Object.keys(el).some((k) => k.startsWith('__reactFiber'));

// Never remove React-owned nodes; drop template defaults / manual duplicates around them
const pickKeep = (list) => list.filter(isReact).pop()
  || list.filter((el) => !el.hasAttribute('data-default') && !el.hasAttribute(OWN)).pop()
  || list[list.length - 1];

const dedupe = (head) => {
  const groups = { title: [...head.querySelectorAll('title')] };
  head.querySelectorAll('meta[name], meta[property]').forEach((m) => {
    const k = metaKey(m);
    if (k) (groups[k] = groups[k] || []).push(m);
  });
  Object.values(groups).forEach((list) => {
    if (list.length < 2) return;
    const keep = pickKeep(list);
    list.forEach((el) => el !== keep && !isReact(el) && el.remove());
  });
};

const upsert = (head, selector, create, attr, value) => {
  let el = head.querySelector(selector);
  if (!el) {
    el = create();
    el.setAttribute(OWN, '');
    head.appendChild(el);
  }
  if (el.getAttribute(attr) !== value) el.setAttribute(attr, value);
};

const metaEl = (keyAttr, key) => () => {
  const m = document.createElement('meta');
  m.setAttribute(keyAttr, key);
  return m;
};

export const syncHead = (pathname) => {
  const head = document.head;
  dedupe(head);
  const url = canonicalUrl(pathname);
  const noindex = /noindex/i.test(head.querySelector('meta[name="robots"]')?.getAttribute('content') || '');

  head.querySelectorAll('link[rel="canonical"]').forEach((l, i) => (i > 0 || noindex) && l.remove());
  if (!noindex) {
    upsert(head, 'link[rel="canonical"]', () => {
      const l = document.createElement('link');
      l.setAttribute('rel', 'canonical');
      return l;
    }, 'href', url);
  }
  upsert(head, 'meta[property="og:url"]', metaEl('property', 'og:url'), 'content', url);

  const title = head.querySelector('title')?.textContent?.trim();
  const desc = head.querySelector('meta[name="description"]')?.getAttribute('content');
  if (title) {
    upsert(head, 'meta[property="og:title"]', metaEl('property', 'og:title'), 'content', title);
    upsert(head, 'meta[name="twitter:title"]', metaEl('name', 'twitter:title'), 'content', title);
  }
  if (desc) {
    upsert(head, 'meta[property="og:description"]', metaEl('property', 'og:description'), 'content', desc);
    upsert(head, 'meta[name="twitter:description"]', metaEl('name', 'twitter:description'), 'content', desc);
  }
};

// One self-referencing canonical + og/twitter tags matching the page's own title/description
export const HeadSync = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    let t = 0;
    const run = () => {
      clearTimeout(t);
      t = setTimeout(() => syncHead(pathname), 0);
    };
    run();
    const obs = new MutationObserver(run);
    obs.observe(document.head, { childList: true, subtree: true, characterData: true, attributes: true });
    return () => {
      obs.disconnect();
      clearTimeout(t);
    };
  }, [pathname]);

  return null;
};
